// ─── Chin Monitor logic hook ────────────────────────────────
// All state + business logic for the Chin Bot Monitor, extracted
// from ChinMonitor.tsx so the component stays a thin render layer.
// Behavior is unchanged — this is a pure code move.

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Strategy } from '@/types/strategy';
import {
  getOrCreateChinEngine,
  getChinEngine,
  type ChinBotSnapshot,
  type ForceCheckSymbolResult,
} from '@/lib/chin-bot-engine';
import { sendFormattedTelegram, TELEGRAM_CATEGORIES } from '@/lib/telegram-sender';
import { useChinWorkspacePersistence, type StatusLevel, type SymbolPlayConfig, type ChinWorkspaceData } from '@/hooks/useChinWorkspacePersistence';
import { useChinRiskControl } from '@/hooks/useChinRiskControl';
import type { RiskSymbolConfig } from '@/lib/chin-risk-controller';
import { type LogEntry } from '@/pages/strategies/components/ActivityLog';
import {
  type OrderLoopStatus,
} from '@/lib/chin-order-executor';
import {
  type OffEngineStatus,
} from '@/lib/chin-off-engine';
import {
  getGlobalOrderState,
  getAllGlobalOrderStates,
  getAllGlobalOffEngineStates,
  startGlobalOrderLoop,
  abortGlobalOrderLoop,
  abortAllGlobalOrderLoops,
  startGlobalOffEngine,
  abortGlobalOffEngine,
  abortAllGlobalOffEngines,
  subscribeGlobalWorkspace,
  setGlobalRiskEvaluation,
  getGlobalOffEngineState,
  subscribeEngineTerminated,
} from '@/lib/chin-global-workspace';
import { CHIN_TIMEFRAME, type CtraderAccount, type SymbolList } from './chin-monitor/types';

interface UseChinMonitorLogicArgs {
  strategy: Strategy;
  ctraderAccounts: CtraderAccount[];
  isActive: boolean;
  initialAccountId?: string;
}

export function useChinMonitorLogic({
  strategy,
  ctraderAccounts,
  isActive,
  initialAccountId,
}: UseChinMonitorLogicArgs) {
  const strategyId = strategy.id || '';

  // ─── State ───

  const [symbolLists, setSymbolLists] = useState<SymbolList[]>([]);
  const [loadingLists, setLoadingLists] = useState(true);

  const [selectedListId, setSelectedListId] = useState<string>('');
  const [selectedList, setSelectedList] = useState<SymbolList | null>(null);

  const [selectedAccountId, setSelectedAccountId] = useState<string>('');
  const [engineState, setEngineState] = useState<ChinBotSnapshot | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [isEngineRunning, setIsEngineRunning] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Per-symbol status dropdown state
  const [statusMap, setStatusMap] = useState<Record<number, StatusLevel>>({});
  // Per-symbol status dropdown open state
  const [openStatusDropdown, setOpenStatusDropdown] = useState<number | null>(null);
  // Per-symbol note modal open state
  const [openNoteSymbol, setOpenNoteSymbol] = useState<number | null>(null);
  // Per-symbol play toggled
  const [playMap, setPlayMap] = useState<Record<number, boolean>>({});
  // Per-symbol play config (conditionPrice + maxOrders) — persisted to Supabase
  const [symbolConfigs, setSymbolConfigs] = useState<Record<number, SymbolPlayConfig>>({});
  // Workspace loading state
  const [workspaceLoading, setWorkspaceLoading] = useState(true);

  // User email for Telegram alerts (looked up from profiles)
  const [telegramEmail, setTelegramEmail] = useState<string>('');

  // ─── Telegram Alert Toggles (engine keeps running; only mute Telegram) ──
  const [tma50AlertOn, setTma50AlertOn] = useState(true);
  const [convergenceAlertOn, setConvergenceAlertOn] = useState(true);
  const [boxVsTma50AlertOn, setBoxVsTma50AlertOn] = useState(true);

  // ─── Risk Control Rule 4 (After Hours daily shutdown) toggle ──
  const [rule4Enabled, setRule4Enabled] = useState(true);

  // ─── Rule 4 Daily Reset Countdown (23:00 GMT+7 = 16:00 UTC) ──
  const [dailyCountdown, setDailyCountdown] = useState<string>('—');
  const [dailyResetFiredToday, setDailyResetFiredToday] = useState(false);
  const [dailyResetFiredAt, setDailyResetFiredAt] = useState<string>('');

  // ─── Force Check All ─────────────────────────────────────
  const [forceCheckResults, setForceCheckResults] = useState<ForceCheckSymbolResult[] | null>(null);
  const [forceCheckRunning, setForceCheckRunning] = useState(false);
  const [forceCheckPanelOpen, setForceCheckPanelOpen] = useState(false);

  // ─── Test Telegram ─────────────────────────────────────
  const [testTelegramRunning, setTestTelegramRunning] = useState(false);
  const [testTelegramResult, setTestTelegramResult] = useState<{ success: boolean; error?: string } | null>(null);

  const unsubscribeRef = useRef<(() => void) | null>(null);
  const startAttemptedRef = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const statusDropdownRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(true);

  // ─── Order execution state ───────────────────────────────
  const [orderStatuses, setOrderStatuses] = useState<Record<number, OrderLoopStatus>>({});

  // ─── Off Engine state ───────────────────────────────────
  const [offEngineStatuses, setOffEngineStatuses] = useState<Record<number, OffEngineStatus>>({});

  // ─── Activity Log ───────────────────────────────────────
  const [activityLog, setActivityLog] = useState<LogEntry[]>([]);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const logIdRef = useRef(0);

  const pushLog = useCallback((level: LogEntry['level'], symbol: string, message: string) => {
    const id = ++logIdRef.current;
    const now = new Date();
    const timestamp = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}:${String(now.getSeconds()).padStart(2, '0')}.${String(now.getMilliseconds()).padStart(3, '0')}`;
    setActivityLog((prev) => {
      const next = [...prev, { id, timestamp, level, symbol, message }];
      // Keep last 300 entries max
      return next.length > 300 ? next.slice(-300) : next;
    });
    // Also mirror to console for devtools
    const prefix = level === 'error' ? '❌' : level === 'warn' ? '⚠️' : level === 'success' ? '✅' : 'ℹ️';
    console.log(`[ActivityLog] ${prefix} [${symbol}] ${message}`);
  }, []);

  // ─── Workspace persistence (Supabase) ─────────────────────

  const effectiveAccountId = selectedAccountId;
  const tradingAccountId = selectedAccountId || selectedList?.ctrader_account_id || '';

  // ─── Realtime cross-device workspace sync handler ──────────
  // Called when ANOTHER device saves this account's workspace. Re-applies the
  // latest config to local state so both devices stay in sync without refresh.
  const handleExternalWorkspace = useCallback((saved: ChinWorkspaceData) => {
    console.log('[ChinMonitor] 🔄 Applying external workspace change (realtime sync)');

    setTma50AlertOn(saved.tma50AlertOn ?? true);
    setConvergenceAlertOn(saved.convergenceAlertOn ?? true);
    setBoxVsTma50AlertOn(saved.boxVsTma50AlertOn ?? true);
    setRule4Enabled(saved.rule4Enabled ?? true);

    // Sync selected account (cross-device account switch).
    if (saved.selectedAccountId && saved.selectedAccountId !== selectedAccountId) {
      setSelectedAccountId(saved.selectedAccountId);
    }

    // Sync selected symbol list (cross-device list switch).
    if (saved.selectedListId && saved.selectedListId !== selectedListId) {
      const newList = symbolLists.find((l) => l.id === saved.selectedListId);
      if (newList) {
        setSelectedListId(saved.selectedListId);
        setSelectedList(newList);
        setDropdownOpen(false);
        setOpenStatusDropdown(null);

        // Re-init the engine with the new list's symbols so the monitor
        // actually shows/updates the newly-selected list on this device.
        const symbolInfos = newList.symbols.map((s) => ({
          symbolId: s.symbolId,
          symbolName: s.symbolName,
          digits: s.digits,
        }));
        const dataAccount = saved.selectedAccountId || newList.ctrader_account_id;
        if (dataAccount) {
          const engine = getOrCreateChinEngine(strategyId, dataAccount);
          engine.init(dataAccount, symbolInfos, CHIN_TIMEFRAME, strategy.chinBotParams || undefined, telegramEmail);
        }
      }
    }

    if (saved.statusMap) setStatusMap(saved.statusMap);
    if (saved.playMap) setPlayMap(saved.playMap);
    if (saved.symbolConfigs) setSymbolConfigs(saved.symbolConfigs);

    // Sync per-symbol status into the engine (affects the Telegram Status label).
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (engine && saved.statusMap) {
      for (const [symIdStr, status] of Object.entries(saved.statusMap)) {
        engine.setSymbolStatus(Number(symIdStr), status);
      }
    }

    pushLog('info', 'SYSTEM', 'Workspace synced from another device (realtime)');
  }, [strategyId, tradingAccountId, pushLog, selectedListId, selectedAccountId, symbolLists, strategy.chinBotParams, telegramEmail]);

  const {
    saveImmediate,
    scheduleSave,
    loadWorkspace,
    cancelPendingSave,
    flushKeepalive,
    saveStatus,
  } = useChinWorkspacePersistence(strategyId, effectiveAccountId, handleExternalWorkspace);

  // ─── Emergency Reset — Risk Control ─────────────────────

  const handleEmergencyReset = useCallback((reason: string) => {
    console.warn('[ChinMonitor] ⚠️ EMERGENCY RESET:', reason);

    abortAllGlobalOrderLoops(strategyId, tradingAccountId);
    abortAllGlobalOffEngines(strategyId, tradingAccountId);
    pushLog('warn', 'SYSTEM', `Emergency reset: all order loops and off engines aborted (reason: ${reason})`);

    const resetConfigs: Record<number, SymbolPlayConfig> = {};
    const resetPlayMap: Record<number, boolean> = {};
    for (const symId of Object.keys(symbolConfigs)) {
      const numId = Number(symId);
      resetConfigs[numId] = {
        ...(symbolConfigs[numId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' }),
        direction: null,
        orderDirection: null,
      };
      resetPlayMap[numId] = false;
    }
    for (const symId of Object.keys(playMap)) {
      const numId = Number(symId);
      if (!(numId in resetPlayMap)) {
        resetPlayMap[numId] = false;
      }
      if (!(numId in resetConfigs)) {
        resetConfigs[numId] = { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' };
      }
    }

    setSymbolConfigs(resetConfigs);
    setPlayMap(resetPlayMap);

    saveImmediate({
      selectedListId,
      selectedAccountId,
      statusMap,
      playMap: resetPlayMap,
      symbolConfigs: resetConfigs,
    });
  }, [symbolConfigs, playMap, statusMap, selectedListId, selectedAccountId, tradingAccountId, saveImmediate, pushLog, strategyId]);

  // ─── Daily Reset (Rule 4) ──────────────────────────────

  const engineStateRefForReset = useRef(engineState);
  engineStateRefForReset.current = engineState;

  const handleDailyReset = useCallback((reason: string) => {
    console.warn('[ChinMonitor] 🕐 DAILY RESET (Rule 4):', reason);
    pushLog('warn', 'SYSTEM', `⏰ Rule 4: Daily reset at 23:00 GMT+7 — turning off all running engines`);

    abortAllGlobalOrderLoops(strategyId, tradingAccountId);
    pushLog('warn', 'SYSTEM', 'Daily reset: all order loops aborted (Rule 4)');

    abortAllGlobalOffEngines(strategyId, tradingAccountId);
    pushLog('warn', 'SYSTEM', 'Daily reset: all off engines aborted (Rule 4)');

    const currentEngineState = engineStateRefForReset.current;
    const dailyResetConfigs: Record<number, SymbolPlayConfig> = {};
    let hasAnyOn = false;
    for (const symId of Object.keys(symbolConfigs)) {
      const numId = Number(symId);
      const cfg = symbolConfigs[numId];
      if (!cfg) continue;
      const isOn = cfg.direction !== null || cfg.orderDirection !== null;
      if (isOn) {
        hasAnyOn = true;
        dailyResetConfigs[numId] = {
          ...cfg,
          direction: null,
          orderDirection: null,
        };
        pushLog('info', currentEngineState?.symbols[numId]?.symbolName || `Symbol #${numId}`, `Rule 4: turned OFF (was ${cfg.direction || 'order=' + cfg.orderDirection})`);
      }
    }

    if (!hasAnyOn) {
      pushLog('info', 'SYSTEM', '⏰ Rule 4: No running engines found — nothing to turn off');
      return;
    }

    setSymbolConfigs((prev) => {
      const merged = { ...prev };
      for (const symId of Object.keys(dailyResetConfigs)) {
        const numId = Number(symId);
        merged[numId] = dailyResetConfigs[numId];
      }
      return merged;
    });

    setPlayMap((prev) => {
      const next = { ...prev };
      for (const symId of Object.keys(dailyResetConfigs)) {
        next[Number(symId)] = false;
      }
      return next;
    });

    saveImmediate({
      selectedListId,
      selectedAccountId,
      statusMap,
      playMap: (() => {
        const next = { ...playMap };
        for (const symId of Object.keys(dailyResetConfigs)) {
          next[Number(symId)] = false;
        }
        return next;
      })(),
      symbolConfigs: (() => {
        const merged = { ...symbolConfigs };
        for (const symId of Object.keys(dailyResetConfigs)) {
          merged[Number(symId)] = dailyResetConfigs[Number(symId)];
        }
        return merged;
      })(),
    });

    pushLog('success', 'SYSTEM', `⏰ Rule 4 complete: all engines turned off`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolConfigs, playMap, statusMap, selectedListId, selectedAccountId, tradingAccountId, saveImmediate, pushLog, strategyId]);

  // ─── Risk Control Hook ──────────────────────────────────

  const activeSymbolIds = selectedList
    ? selectedList.symbols.map((s) => s.symbolId)
    : [];

  const riskControl = useChinRiskControl({
    accountId: tradingAccountId,
    engineState,
    symbolConfigs: symbolConfigs as Record<number, RiskSymbolConfig>,
    activeSymbolIds,
    onEmergencyReset: handleEmergencyReset,
    onDailyReset: handleDailyReset,
    rule4Enabled,
  });

  useEffect(() => {
    if (riskControl.evaluation) {
      setGlobalRiskEvaluation(
        strategyId,
        tradingAccountId,
        riskControl.evaluation,
        symbolConfigs as Record<number, RiskSymbolConfig>,
      );
    }
  }, [strategyId, tradingAccountId, riskControl.evaluation, symbolConfigs]);

  // ─── Global workspace subscription ──────────────────────────

  useEffect(() => {
    if (!strategyId) return;

    const orderMap = getAllGlobalOrderStates(strategyId, tradingAccountId);
    const offMap = getAllGlobalOffEngineStates(strategyId, tradingAccountId);

    if (orderMap.size > 0) {
      const synced: Record<number, OrderLoopStatus> = {};
      orderMap.forEach((entry, symId) => {
        if (entry.status) synced[symId] = entry.status;
      });
      if (Object.keys(synced).length > 0) {
        setOrderStatuses((prev) => ({ ...prev, ...synced }));
        console.log(`[ChinMonitor] Synced ${Object.keys(synced).length} order loop status(es) from global workspace`);
      }
    }

    if (offMap.size > 0) {
      const synced: Record<number, OffEngineStatus> = {};
      offMap.forEach((entry, symId) => {
        if (entry.status) synced[symId] = entry.status;
      });
      if (Object.keys(synced).length > 0) {
        setOffEngineStatuses((prev) => ({ ...prev, ...synced }));
        console.log(`[ChinMonitor] Synced ${Object.keys(synced).length} off engine status(es) from global workspace`);
      }
    }

    const unsub = subscribeGlobalWorkspace(() => {
      const freshOrderMap = getAllGlobalOrderStates(strategyId, tradingAccountId);
      const freshOffMap = getAllGlobalOffEngineStates(strategyId, tradingAccountId);

      startTransition(() => {
        setOrderStatuses((prev) => {
          const next = { ...prev };
          freshOrderMap.forEach((entry, symId) => {
            if (entry.status) {
              next[symId] = entry.status;
            } else {
              delete next[symId];
            }
          });
          return next;
        });

        setOffEngineStatuses((prev) => {
          const next = { ...prev };
          freshOffMap.forEach((entry, symId) => {
            if (entry.status) {
              next[symId] = entry.status;
            } else {
              delete next[symId];
            }
          });
          return next;
        });
      });
    });

    return unsub;
  }, [strategyId, tradingAccountId]);

  // ─── Load symbol lists from Supabase ─────────────────────────

  useEffect(() => {
    let cancelled = false;
    async function loadLists() {
      setLoadingLists(true);
      try {
        const { data, error } = await supabase
          .from('symbol_lists')
          .select('id, name, ctrader_account_id, symbols')
          .order('name');

        if (error) throw error;
        if (!cancelled && data) {
          setSymbolLists(data as SymbolList[]);
        }
      } catch (err) {
        console.warn('[ChinMonitor] Failed to load symbol lists:', err);
      } finally {
        if (!cancelled) setLoadingLists(false);
      }
    }
    loadLists();
    return () => { cancelled = true; };
  }, []);

  // ─── Restore workspace from Supabase ──────────────────────

  const lastRestoredAccountRef = useRef<string>('');

  useEffect(() => {
    if (lastRestoredAccountRef.current === effectiveAccountId) return;
    if (loadingLists || symbolLists.length === 0) return;

    let cancelled = false;

    async function restore() {
      const saved = await loadWorkspace();
      if (cancelled) return;

      let tmaVal = saved?.tma50AlertOn;
      let convVal = saved?.convergenceAlertOn;
      if (tmaVal === undefined && tradingAccountId) {
        const old = localStorage.getItem(`chin-alert-tma50-${tradingAccountId}`);
        tmaVal = old === null ? true : old === '1';
        localStorage.removeItem(`chin-alert-tma50-${tradingAccountId}`);
      }
      if (convVal === undefined && tradingAccountId) {
        const old = localStorage.getItem(`chin-alert-convergence-${tradingAccountId}`);
        convVal = old === null ? true : old === '1';
        localStorage.removeItem(`chin-alert-convergence-${tradingAccountId}`);
      }
      setTma50AlertOn(tmaVal ?? true);
      setConvergenceAlertOn(convVal ?? true);
      setBoxVsTma50AlertOn(saved?.boxVsTma50AlertOn ?? true);
      setRule4Enabled(saved?.rule4Enabled ?? true);

      if (!saved || !saved.selectedListId) {
        lastRestoredAccountRef.current = effectiveAccountId;
        setSelectedListId('');
        setSelectedList(null);
        setStatusMap({});
        setPlayMap({});
        setSymbolConfigs({});
        setOpenStatusDropdown(null);
        startAttemptedRef.current = false;
        setWorkspaceLoading(false);
        return;
      }

      const savedList = symbolLists.find((l) => l.id === saved.selectedListId);
      if (!savedList) {
        lastRestoredAccountRef.current = effectiveAccountId;
        setSelectedListId('');
        setSelectedList(null);
        setStatusMap({});
        setPlayMap({});
        setSymbolConfigs({});
        setOpenStatusDropdown(null);
        startAttemptedRef.current = false;
        setWorkspaceLoading(false);
        return;
      }

      lastRestoredAccountRef.current = effectiveAccountId;

      setSelectedListId(saved.selectedListId);
      setSelectedList(savedList);

      if (saved.selectedAccountId) {
        setSelectedAccountId(saved.selectedAccountId);
      }

      if (saved.statusMap && Object.keys(saved.statusMap).length > 0) {
        setStatusMap(saved.statusMap);
      }
      if (saved.playMap && Object.keys(saved.playMap).length > 0) {
        setPlayMap(saved.playMap);
      }
      if (saved.symbolConfigs && Object.keys(saved.symbolConfigs).length > 0) {
        setSymbolConfigs(saved.symbolConfigs);
      }

      const symbolInfos = savedList.symbols.map((s) => ({
        symbolId: s.symbolId,
        symbolName: s.symbolName,
        digits: s.digits,
      }));

      const accountToUse = saved.selectedAccountId || savedList.ctrader_account_id;
      if (accountToUse) {
        const engine = getOrCreateChinEngine(strategyId, accountToUse);
        engine.init(accountToUse, symbolInfos, CHIN_TIMEFRAME, strategy.chinBotParams || undefined, telegramEmail || undefined);
      }

      setWorkspaceLoading(false);
      console.log('[ChinMonitor] Workspace restored from Supabase:', saved.selectedListId);
    }

    restore();
    return () => { cancelled = true; };
  }, [loadingLists, symbolLists, strategyId, strategy.chinBotParams, telegramEmail, loadWorkspace, effectiveAccountId]);

  // ─── Look up user email for Telegram alerts ─────────────────

  useEffect(() => {
    let cancelled = false;
    async function loadEmail() {
      if (!strategy.user_id) return;
      try {
        const { data, error } = await supabase
          .from('profiles')
          .select('email')
          .eq('id', strategy.user_id)
          .maybeSingle();

        if (error) throw error;
        if (!cancelled && data?.email) {
          setTelegramEmail(data.email);
        }
      } catch (err) {
        console.warn('[ChinMonitor] Failed to load user email:', err);
      }
    }
    loadEmail();
    return () => { cancelled = true; };
  }, [strategy.user_id]);

  // ─── Sync telegramEmail into ChinBotEngine ──────────────────

  useEffect(() => {
    if (!telegramEmail || !strategyId) return;
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (engine) {
      engine.setTelegramEmail(telegramEmail);
      pushLog('info', 'SYSTEM', `Telegram target: ${telegramEmail}`);
    }
  }, [telegramEmail, strategyId, tradingAccountId, pushLog]);

  // ─── Sync toggle state into the engine (per account) ────
  useEffect(() => {
    if (!strategyId || !tradingAccountId) return;
    const engine = getOrCreateChinEngine(strategyId, tradingAccountId);
    engine.setTma50AlertEnabled(tma50AlertOn);
    engine.setConvergenceAlertEnabled(convergenceAlertOn);
    engine.setBoxVsTma50AlertEnabled(boxVsTma50AlertOn);
  }, [strategyId, tradingAccountId, tma50AlertOn, convergenceAlertOn, boxVsTma50AlertOn]);

  // ─── Sync per-symbol Box threshold (x) + alert on/off into the engine ──
  useEffect(() => {
    if (!strategyId || !tradingAccountId) return;
    const engine = getOrCreateChinEngine(strategyId, tradingAccountId);
    for (const [symIdStr, cfg] of Object.entries(symbolConfigs)) {
      const symId = Number(symIdStr);
      const parsed = parseFloat(cfg.boxX || '');
      engine.setBoxPipsX(symId, Number.isFinite(parsed) ? parsed : 10);
      engine.setBoxAlertEnabled(symId, cfg.boxAlertOn !== false);
      engine.setSymbolNote(symId, cfg.note || '');
    }
  }, [strategyId, tradingAccountId, symbolConfigs]);

  // ─── Telegram alert toggle handlers ──────────────────────
  const handleToggleTma50Alert = useCallback(() => {
    const next = !tma50AlertOn;
    setTma50AlertOn(next);
    saveImmediate({
      selectedListId,
      selectedAccountId,
      statusMap,
      playMap,
      symbolConfigs,
      tma50AlertOn: next,
      convergenceAlertOn,
    });
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (engine) engine.setTma50AlertEnabled(next);
    pushLog('info', 'SYSTEM', `TMA50 Telegram alerts ${next ? 'ENABLED' : 'MUTED'} — engine keeps running`);
  }, [tma50AlertOn, convergenceAlertOn, strategyId, tradingAccountId, selectedListId, selectedAccountId, statusMap, playMap, symbolConfigs, saveImmediate, pushLog]);

  const handleToggleConvergenceAlert = useCallback(() => {
    const next = !convergenceAlertOn;
    setConvergenceAlertOn(next);
    saveImmediate({
      selectedListId,
      selectedAccountId,
      statusMap,
      playMap,
      symbolConfigs,
      tma50AlertOn,
      convergenceAlertOn: next,
    });
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (engine) engine.setConvergenceAlertEnabled(next);
    pushLog('info', 'SYSTEM', `Tụ Tách Telegram alerts ${next ? 'ENABLED' : 'MUTED'} — engine keeps running`);
  }, [convergenceAlertOn, tma50AlertOn, strategyId, tradingAccountId, selectedListId, selectedAccountId, statusMap, playMap, symbolConfigs, saveImmediate, pushLog]);

  const handleToggleBoxVsTma50Alert = useCallback(() => {
    const next = !boxVsTma50AlertOn;
    setBoxVsTma50AlertOn(next);
    saveImmediate({
      selectedListId,
      selectedAccountId,
      statusMap,
      playMap,
      symbolConfigs,
      tma50AlertOn,
      convergenceAlertOn,
      boxVsTma50AlertOn: next,
    });
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (engine) engine.setBoxVsTma50AlertEnabled(next);
    pushLog('info', 'SYSTEM', `Box vs TMA50 Telegram alerts ${next ? 'ENABLED' : 'MUTED'} — engine keeps running`);
  }, [boxVsTma50AlertOn, tma50AlertOn, convergenceAlertOn, strategyId, tradingAccountId, selectedListId, selectedAccountId, statusMap, playMap, symbolConfigs, saveImmediate, pushLog]);

  // ─── Risk Control Rule 4 toggle handler ─────────────────
  const handleToggleRule4 = useCallback(() => {
    const next = !rule4Enabled;
    setRule4Enabled(next);
    saveImmediate({
      selectedListId,
      selectedAccountId,
      statusMap,
      playMap,
      symbolConfigs,
      tma50AlertOn,
      convergenceAlertOn,
      boxVsTma50AlertOn,
      rule4Enabled: next,
    });
    pushLog('info', 'SYSTEM', `Risk Control Rule 4 (After Hours daily shutdown) ${next ? 'ENABLED' : 'DISABLED'}`);
  }, [rule4Enabled, tma50AlertOn, convergenceAlertOn, boxVsTma50AlertOn, strategyId, tradingAccountId, selectedListId, selectedAccountId, statusMap, playMap, symbolConfigs, saveImmediate, pushLog]);

  // ─── Sync pushLog to ChinBotEngine ──────────────────────

  useEffect(() => {
    if (!strategyId) return;
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (engine) {
      engine.setOnLog(pushLog);
    }
  }, [strategyId, tradingAccountId, pushLog]);

  // ─── Subscribe to engine ─────────────────────────────────────

  useEffect(() => {
    if (!strategyId || !tradingAccountId) return;
    mountedRef.current = true;

    const engine = getOrCreateChinEngine(strategyId, tradingAccountId);
    engine.setOnLog(pushLog);
    if (telegramEmail) engine.setTelegramEmail(telegramEmail);
    setIsEngineRunning(engine.isRunning);

    unsubscribeRef.current = engine.subscribe((s) => {
      if (!mountedRef.current) return;
      startTransition(() => {
        setEngineState(s);
        setIsEngineRunning(s.isRunning);
      });
    });

    setInitialized(true);

    return () => {
      mountedRef.current = false;
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [strategyId, tradingAccountId, pushLog, telegramEmail]);

  // ─── Auto-select account ────────────────────────────────────

  useEffect(() => {
    if (ctraderAccounts.length === 0) return;

    if (initialAccountId) {
      const match = ctraderAccounts.find((a) => String(a.account_id) === String(initialAccountId));
      if (match) {
        setSelectedAccountId(match.account_id);
        return;
      }
    }

    if (strategy.active_accounts && strategy.active_accounts.length > 0) {
      const firstActive = strategy.active_accounts[0];
      const match = ctraderAccounts.find((a) => String(a.account_id) === String(firstActive));
      if (match) {
        setSelectedAccountId(match.account_id);
        return;
      }
    }

    setSelectedAccountId(ctraderAccounts[0].account_id);
  }, [ctraderAccounts, strategyId, strategy.active_accounts, initialAccountId]);

  // ─── Handle symbol list selection ───────────────────────────

  const handleSelectList = useCallback((list: SymbolList) => {
    setSelectedListId(list.id);
    setSelectedList(list);
    setDropdownOpen(false);
    const newStatusMap: Record<number, StatusLevel> = {};
    const newPlayMap: Record<number, boolean> = {};
    const newSymbolConfigs: Record<number, SymbolPlayConfig> = {};
    setStatusMap(newStatusMap);
    setPlayMap(newPlayMap);
    setSymbolConfigs(newSymbolConfigs);
    setOpenStatusDropdown(null);

    const symbolInfos = list.symbols.map((s) => ({
      symbolId: s.symbolId,
      symbolName: s.symbolName,
      digits: s.digits,
    }));

    const dataAccount = selectedAccountId || list.ctrader_account_id;
    if (dataAccount) {
      const engine = getOrCreateChinEngine(strategyId, dataAccount);
      engine.init(dataAccount, symbolInfos, CHIN_TIMEFRAME, strategy.chinBotParams || undefined, telegramEmail);
    }

    saveImmediate({
      selectedListId: list.id,
      selectedAccountId,
      statusMap: newStatusMap,
      playMap: newPlayMap,
      symbolConfigs: newSymbolConfigs,
    });
  }, [strategyId, selectedAccountId, strategy.chinBotParams, telegramEmail, saveImmediate]);

  // ─── Start engine ───────────────────────────────────────────

  const startEngine = useCallback(async () => {
    const acctToUse = selectedAccountId || selectedList?.ctrader_account_id;
    if (!acctToUse || !strategyId || !selectedList) return;

    const engine = getOrCreateChinEngine(strategyId, acctToUse);

    if (engine.isRunning) return;

    const symbolInfos = selectedList.symbols.map((s) => ({
      symbolId: s.symbolId,
      symbolName: s.symbolName,
      digits: s.digits,
    }));

    await engine.init(acctToUse, symbolInfos, CHIN_TIMEFRAME, strategy.chinBotParams || undefined, telegramEmail);
    engine.start();
  }, [selectedAccountId, selectedList, strategyId, strategy.chinBotParams, telegramEmail]);

  useEffect(() => {
    if (selectedList && selectedAccountId && initialized && isActive && !startAttemptedRef.current) {
      startAttemptedRef.current = true;
      startEngine();
    }
  }, [selectedList, selectedAccountId, initialized, isActive, startEngine]);

  useEffect(() => {
    startAttemptedRef.current = false;
  }, [selectedAccountId, selectedList]);

  // ─── Handle isActive changes ────────────────────────────────

  useEffect(() => {
    if (!strategyId || !initialized) return;
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (!engine) return;

    if (isActive) {
      if (!engine.isRunning && tradingAccountId && selectedList) {
        startEngine();
      }
    }
  }, [isActive, strategyId, initialized, tradingAccountId, selectedList, startEngine]);

  // ─── Close dropdowns on outside click ────────────────────────

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(e.target as Node)) {
        setOpenStatusDropdown(null);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // ─── Status handlers ────────────────────────────────────────

  const handleStatusChange = useCallback((symbolId: number, level: StatusLevel) => {
    setStatusMap((prev) => {
      const next = { ...prev, [symbolId]: level };
      saveImmediate({
        selectedListId,
        selectedAccountId,
        statusMap: next,
        playMap,
        symbolConfigs,
      });
      return next;
    });
    setOpenStatusDropdown(null);
    const engine = getChinEngine(strategyId, tradingAccountId);
    if (engine) {
      engine.setSymbolStatus(symbolId, level);
    }
  }, [strategyId, tradingAccountId, selectedListId, selectedAccountId, playMap, symbolConfigs, saveImmediate]);

  // ─── Play config handlers (debounced — rapid typing) ─────

  const handlePriceChange = useCallback((symbolId: number, val: string) => {
    setSymbolConfigs((prev) => {
      const next = {
        ...prev,
        [symbolId]: { ...(prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' }), conditionPrice: val },
      };
      scheduleSave({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap,
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, statusMap, playMap, scheduleSave]);

  const handleMaxChange = useCallback((symbolId: number, val: string) => {
    setSymbolConfigs((prev) => {
      const next = {
        ...prev,
        [symbolId]: { ...(prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' }), maxOrders: val },
      };
      scheduleSave({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap,
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, statusMap, playMap, scheduleSave]);

  // ─── Direction handler ──────────────────────────────────

  const handleDirectionChange = useCallback((symbolId: number, dir: 'buy' | 'sell' | null) => {
    if (dir === null) {
      abortGlobalOffEngine(strategyId, tradingAccountId, symbolId);
      pushLog('warn', engineState?.symbols[symbolId]?.symbolName || `Symbol #${symbolId}`, 'Aborting off engine (direction set to null by user)');
    }

    setPlayMap((prevPlay) => ({ ...prevPlay, [symbolId]: dir !== null }));
    setSymbolConfigs((prev) => {
      const next = {
        ...prev,
        [symbolId]: { ...(prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' }), direction: dir },
      };
      saveImmediate({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap: { ...playMap, [symbolId]: dir !== null },
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, tradingAccountId, statusMap, playMap, saveImmediate, pushLog, engineState, strategyId]);

  // ─── Order Direction handler ──────────────────────────────

  const handleOrderDirectionChange = useCallback((symbolId: number, dir: 'buy' | 'sell' | null) => {
    if (dir === null) {
      abortGlobalOrderLoop(strategyId, tradingAccountId, symbolId);
      pushLog('warn', engineState?.symbols[symbolId]?.symbolName || `Symbol #${symbolId}`, 'Aborting order loop (direction set to null by user)');
    }

    setSymbolConfigs((prev) => {
      const next = {
        ...prev,
        [symbolId]: { ...(prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' }), orderDirection: dir },
      };
      saveImmediate({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap,
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, tradingAccountId, statusMap, playMap, saveImmediate, pushLog, engineState, strategyId]);

  // ─── Volume handler ────────────────────────────────────

  const handleVolumeChange = useCallback((symbolId: number, val: string) => {
    setSymbolConfigs((prev) => {
      const next = {
        ...prev,
        [symbolId]: { ...(prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' }), orderVolume: val },
      };
      scheduleSave({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap,
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, statusMap, playMap, scheduleSave]);

  // ─── Box threshold (x) handler ───────────────────────────

  const handleBoxXChange = useCallback((symbolId: number, val: string) => {
    setSymbolConfigs((prev) => {
      const next = {
        ...prev,
        [symbolId]: { ...(prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' }), boxX: val },
      };
      scheduleSave({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap,
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, statusMap, playMap, scheduleSave]);

  // ─── Box Telegram alert toggle (per-symbol) ──────────────
  // Only mutes Telegram for that symbol; the Box engine keeps running
  // and detecting for it regardless of this switch.
  const handleBoxAlertToggle = useCallback((symbolId: number) => {
    setSymbolConfigs((prev) => {
      const existing = prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' };
      const next = {
        ...prev,
        [symbolId]: { ...existing, boxAlertOn: !(existing.boxAlertOn !== false) },
      };
      saveImmediate({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap,
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, statusMap, playMap, saveImmediate]);

  // ─── Note handler (per-symbol free-text note) ──────────

  const handleNoteChange = useCallback((symbolId: number, note: string) => {
    setSymbolConfigs((prev) => {
      const existing = prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' };
      const next = {
        ...prev,
        [symbolId]: { ...existing, note },
      };
      saveImmediate({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap,
        symbolConfigs: next,
      });
      return next;
    });
  }, [selectedListId, selectedAccountId, statusMap, playMap, saveImmediate]);

  // ─── Reset symbol handler ─────────────────────────────

  const handleResetSymbol = useCallback((symbolId: number) => {
    const symName = engineState?.symbols[symbolId]?.symbolName || `Symbol #${symbolId}`;

    abortGlobalOrderLoop(strategyId, tradingAccountId, symbolId);
    abortGlobalOffEngine(strategyId, tradingAccountId, symbolId);
    pushLog('info', symName, 'Reset params — play settings cleared (volume preserved)');

    setSymbolConfigs((prev) => {
      const existing = prev[symbolId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' };
      const next = {
        ...prev,
        [symbolId]: {
          conditionPrice: '',
          maxOrders: '',
          direction: null,
          orderDirection: null,
          orderVolume: existing.orderVolume || '',
          boxX: existing.boxX || '',
          boxAlertOn: existing.boxAlertOn,
          note: existing.note,
        },
      };
      setPlayMap((prevPlay) => ({ ...prevPlay, [symbolId]: false }));
      saveImmediate({
        selectedListId,
        selectedAccountId,
        statusMap,
        playMap: { ...playMap, [symbolId]: false },
        symbolConfigs: next,
      });
      return next;
    });
  }, [strategyId, tradingAccountId, selectedListId, selectedAccountId, statusMap, playMap, saveImmediate, pushLog, engineState]);

  // ─── Reset All handler ─────────────────────────────

  const handleResetAll = useCallback(() => {
    const symIds = activeSymbolIds;
    if (symIds.length === 0) {
      pushLog('warn', 'SYSTEM', 'Reset All: no symbols in list to reset');
      return;
    }

    abortAllGlobalOrderLoops(strategyId, tradingAccountId);
    abortAllGlobalOffEngines(strategyId, tradingAccountId);
    pushLog('warn', 'SYSTEM', `Reset All — clearing play setup for ${symIds.length} symbol(s)`);

    const resetConfigs: Record<number, SymbolPlayConfig> = {};
    const resetPlayMap: Record<number, boolean> = {};
    for (const symId of symIds) {
      const existing = symbolConfigs[symId] || { conditionPrice: '', maxOrders: '', direction: null, orderDirection: null, orderVolume: '' };
      resetConfigs[symId] = {
        conditionPrice: '',
        maxOrders: '',
        direction: null,
        orderDirection: null,
        orderVolume: existing.orderVolume || '',
        boxX: existing.boxX || '',
        boxAlertOn: existing.boxAlertOn,
        note: existing.note,
      };
      resetPlayMap[symId] = false;
    }

    setSymbolConfigs(resetConfigs);
    setPlayMap(resetPlayMap);

    saveImmediate({
      selectedListId,
      selectedAccountId,
      statusMap,
      playMap: resetPlayMap,
      symbolConfigs: resetConfigs,
    });

    pushLog('success', 'SYSTEM', `Reset All complete — ${symIds.length} symbol(s) cleared`);
  }, [activeSymbolIds, strategyId, tradingAccountId, selectedListId, selectedAccountId, statusMap, symbolConfigs, saveImmediate, pushLog]);

  // ─── Force Check All handler ────────────────────────────

  const handleForceCheckAll = useCallback(async () => {
    if (forceCheckRunning) return;

    const engine = getChinEngine(strategyId, tradingAccountId);
    if (!engine) return;

    setForceCheckRunning(true);
    setForceCheckPanelOpen(true);

    try {
      const snapshot = engine.snapshot();
      const allBuffersSmall = Object.values(snapshot.symbols).every(
        (buf) => buf.buffer.length < 50
      );

      if (allBuffersSmall && snapshot.symbolIds.length > 0) {
        console.log('[ChinMonitor] Buffers too small, refreshing historical data...');
        await engine.refreshBuffers();
      }

      const results = engine.forceCheckAllSymbols();
      setForceCheckResults(results);
    } catch (err) {
      console.warn('[ChinMonitor] Force check error:', err);
      setForceCheckResults(null);
    } finally {
      setForceCheckRunning(false);
    }
  }, [strategyId, tradingAccountId, forceCheckRunning]);

  // ─── Test Telegram handler ───────────────────────────────

  const handleTestTelegram = useCallback(async () => {
    if (testTelegramRunning) return;

    setTestTelegramRunning(true);
    setTestTelegramResult(null);

    const targetEmail = telegramEmail || 'admin@chin.vn';
    console.log(`[ChinMonitor] 🧪 Sending TEST Telegram to ${targetEmail}...`);

    try {
      const result = await sendFormattedTelegram(
        {
          timeframe: 'TEST',
          symbol: 'SYSTEM',
          signal: 'NEUTRAL',
          price: 0,
          strategy: 'Chin Bot — Telegram Test',
          message: `This is a manual test from ChinMonitor.\nTimestamp: ${new Date().toISOString()}\nAccount: ${selectedAccountId || 'N/A'}\nList: ${selectedList?.name || 'N/A'}`,
        },
        { category: TELEGRAM_CATEGORIES.TEST, targetEmail },
      );

      console.log(`[ChinMonitor] 🧪 Test Telegram result:`, JSON.stringify(result));
      setTestTelegramResult(result);
    } catch (err) {
      const errMsg = err instanceof Error ? err.message : String(err);
      console.error(`[ChinMonitor] 🧪 Test Telegram error:`, errMsg);
      setTestTelegramResult({ success: false, error: errMsg });
    } finally {
      setTestTelegramRunning(false);
    }
  }, [testTelegramRunning, telegramEmail, selectedAccountId, selectedList]);

  // ─── beforeunload: flush pending save ─────────────────────

  useEffect(() => {
    const handler = () => {
      cancelPendingSave();
      flushKeepalive();
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [flushKeepalive, cancelPendingSave]);

  // ─── Cleanup debounce timer on unmount ───────────────────

  useEffect(() => {
    return () => {
      cancelPendingSave();
    };
  }, [cancelPendingSave]);

  // ─── Order Execution Loop ─────────────────────────────

  const symbolConfigsRef = useRef(symbolConfigs);
  symbolConfigsRef.current = symbolConfigs;

  const accountRef = useRef(tradingAccountId);
  accountRef.current = tradingAccountId;

  const activeIdsRef = useRef(activeSymbolIds);
  activeIdsRef.current = activeSymbolIds;

  const engineRef = useRef(engineState);
  engineRef.current = engineState;

  const pushLogRef = useRef(pushLog);
  pushLogRef.current = pushLog;

  const playMapRef = useRef(playMap);
  playMapRef.current = playMap;

  const statusMapRef = useRef(statusMap);
  statusMapRef.current = statusMap;

  const selectedListIdRef = useRef(selectedListId);
  selectedListIdRef.current = selectedListId;

  const selectedAccountIdRef = useRef(selectedAccountId);
  selectedAccountIdRef.current = selectedAccountId;

  useEffect(() => {
    const accountToUse = accountRef.current;
    if (!accountToUse) {
      pushLogRef.current('warn', 'SYSTEM', 'No account selected, skipping order check');
      return;
    }

    const currentActiveIds = activeIdsRef.current;

    for (const symId of currentActiveIds) {
      const config = symbolConfigsRef.current[symId];
      if (!config) continue;

      const symName = engineRef.current?.symbols[symId]?.symbolName || `Symbol #${symId}`;
      const dir = config.orderDirection || null;
      const maxOrders = parseInt(config.maxOrders, 10) || 0;
      const volume = parseFloat(config.orderVolume) || 0;

      if (!dir) {
        // Direction set to OFF — ensure any running order loop is stopped.
        // This makes the reconcile effect authoritative so "off" always
        // converges the engine state, not just the UI button.
        if (getGlobalOrderState(strategyId, accountToUse, symId)?.abort) {
          abortGlobalOrderLoop(strategyId, accountToUse, symId);
        }
        continue;
      }

      const globalOrderState = getGlobalOrderState(strategyId, accountToUse, symId);
      if (globalOrderState?.abort) {
        const currentStatus = globalOrderState.status;
        const isFinished = currentStatus && (
          currentStatus.phase === 'completed' ||
          currentStatus.phase === 'max_reached' ||
          currentStatus.phase === 'error'
        );

        if (!isFinished) continue;
        abortGlobalOrderLoop(strategyId, accountToUse, symId);
      }

      if (maxOrders <= 0) {
        pushLogRef.current('warn', symName, `Order SKIPPED: maxOrders=${maxOrders} — must be > 0`);
        continue;
      }
      if (volume <= 0) {
        pushLogRef.current('warn', symName, `Order SKIPPED: volume=${volume} — must be > 0`);
        continue;
      }

      startGlobalOrderLoop(
        strategyId,
        symId,
        symName,
        accountToUse,
        dir,
        volume,
        maxOrders,
        pushLogRef.current,
      );
    }

    // ─── Off Engine: start/stop based on direction (Off button) ───
    for (const symId of currentActiveIds) {
      const config = symbolConfigsRef.current[symId];
      if (!config) continue;

      const symName = engineRef.current?.symbols[symId]?.symbolName || `Symbol #${symId}`;
      const dir = config.direction || null;
      const conditionPrice = parseFloat(config.conditionPrice) || 0;
      const maxOrders = parseInt(config.maxOrders, 10) || 0;
      const volume = parseFloat(config.orderVolume) || 0;

      if (!dir) {
        // Direction set to OFF — ensure any running off engine is stopped.
        if (getGlobalOffEngineState(strategyId, accountToUse, symId)?.abort) {
          abortGlobalOffEngine(strategyId, accountToUse, symId);
        }
        continue;
      }

      const globalOffState = getGlobalOffEngineState(strategyId, accountToUse, symId);
      if (globalOffState?.abort) {
        const currentStatus = globalOffState.status;
        const isFinished = currentStatus && (
          currentStatus.phase === 'completed' ||
          currentStatus.phase === 'auto_off' ||
          currentStatus.phase === 'error'
        );
        if (!isFinished) continue;
        abortGlobalOffEngine(strategyId, accountToUse, symId);
      }

      if (conditionPrice <= 0) {
        pushLogRef.current('warn', symName, `Off engine SKIPPED: conditionPrice=${conditionPrice} — must be > 0`);
        continue;
      }
      if (maxOrders <= 0) {
        pushLogRef.current('warn', symName, `Off engine SKIPPED: maxOrders=${maxOrders} — must be > 0`);
        continue;
      }
      if (volume <= 0) {
        pushLogRef.current('warn', symName, `Off engine SKIPPED: volume=${volume} — must be > 0`);
        continue;
      }

      startGlobalOffEngine(
        strategyId,
        symId,
        symName,
        accountToUse,
        dir,
        conditionPrice,
        volume,
        maxOrders,
        pushLogRef.current,
      );
    }

    const activeSet = new Set(currentActiveIds);

    const orderMap = getAllGlobalOrderStates(strategyId, accountToUse);
    orderMap.forEach((entry, symId) => {
      if (!activeSet.has(symId) && entry.abort) {
        pushLogRef.current('info', entry.symbolName || `Symbol #${symId}`, 'Symbol removed from active list, aborting order loop via global workspace');
        abortGlobalOrderLoop(strategyId, accountToUse, symId);
      }
    });

    const offMap = getAllGlobalOffEngineStates(strategyId, accountToUse);
    offMap.forEach((entry, symId) => {
      if (!activeSet.has(symId) && entry.abort) {
        pushLogRef.current('info', entry.symbolName || `Symbol #${symId}`, 'Symbol removed from active list, aborting off engine via global workspace');
        abortGlobalOffEngine(strategyId, accountToUse, symId);
      }
    });
  }, [symbolConfigs]);

  // ─── Reset button when an engine terminates on its own ────

  useEffect(() => {
    if (!strategyId) return;

    return subscribeEngineTerminated((sid, accId, symId, kind) => {
      if (sid !== strategyId) return;
      if (accId !== accountRef.current) return;

      const cfg = symbolConfigsRef.current[symId];
      if (!cfg) return;

      const symName = engineRef.current?.symbols[symId]?.symbolName || `Symbol #${symId}`;

      if (kind === 'order') {
        if (cfg.orderDirection === null) return;
        const nextConfigs = {
          ...symbolConfigsRef.current,
          [symId]: { ...cfg, orderDirection: null },
        };
        setSymbolConfigs(nextConfigs);
        saveImmediate({
          selectedListId: selectedListIdRef.current,
          selectedAccountId: selectedAccountIdRef.current,
          statusMap: statusMapRef.current,
          playMap: playMapRef.current,
          symbolConfigs: nextConfigs,
        });
        pushLogRef.current('info', symName, 'Order loop finished on its own — Order button reset to OFF');
      } else {
        if (cfg.direction === null) return;
        const nextConfigs = {
          ...symbolConfigsRef.current,
          [symId]: { ...cfg, direction: null },
        };
        const nextPlayMap = { ...playMapRef.current, [symId]: false };
        setSymbolConfigs(nextConfigs);
        setPlayMap(nextPlayMap);
        saveImmediate({
          selectedListId: selectedListIdRef.current,
          selectedAccountId: selectedAccountIdRef.current,
          statusMap: statusMapRef.current,
          playMap: nextPlayMap,
          symbolConfigs: nextConfigs,
        });
        pushLogRef.current('info', symName, 'Off engine finished on its own — Off button reset to OFF');
      }
    });
  }, [strategyId, saveImmediate]);

  // ─── Reset buttons to OFF BEFORE risk control blocks them ──

  const prevBlockedMapRef = useRef<Record<number, boolean>>({});

  useEffect(() => {
    const evaluation = riskControl.evaluation;
    if (!evaluation) return;

    const accountToUse = accountRef.current;
    if (!accountToUse) return;

    const currentActiveIds = activeIdsRef.current;
    const blockedNow: Record<number, boolean> = {};
    const nextConfigs = { ...symbolConfigsRef.current };
    const nextPlayMap = { ...playMapRef.current };
    let hasReset = false;

    for (const symId of currentActiveIds) {
      const check = evaluation.checks[symId];
      const blocked = !!check && check.allowed === false && check.blockedBy !== 'spread';
      blockedNow[symId] = blocked;

      const wasBlocked = prevBlockedMapRef.current[symId] || false;
      if (!blocked || wasBlocked) continue;

      const cfg = nextConfigs[symId];
      if (!cfg || (cfg.direction === null && cfg.orderDirection === null)) continue;

      const symName = engineRef.current?.symbols[symId]?.symbolName || `Symbol #${symId}`;

      abortGlobalOrderLoop(strategyId, accountToUse, symId);
      abortGlobalOffEngine(strategyId, accountToUse, symId);

      nextConfigs[symId] = { ...cfg, direction: null, orderDirection: null };
      nextPlayMap[symId] = false;
      hasReset = true;
      pushLogRef.current('warn', symName, `Risk block — resetting button to OFF (${check.reason})`);
    }

    prevBlockedMapRef.current = blockedNow;

    if (hasReset) {
      setSymbolConfigs(nextConfigs);
      setPlayMap(nextPlayMap);
      saveImmediate({
        selectedListId: selectedListIdRef.current,
        selectedAccountId: selectedAccountIdRef.current,
        statusMap: statusMapRef.current,
        playMap: nextPlayMap,
        symbolConfigs: nextConfigs,
      });
    }
  }, [riskControl.evaluation, strategyId, saveImmediate]);

  // ─── Rule 4: Daily reset countdown UI ──────────────────

  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const resetToday = new Date(Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        16, 0, 0, 0
      ));

      const todayKey = now.toISOString().slice(0, 10);
      const fired = riskControl.dailyResetDate === todayKey;

      if (fired && riskControl.dailyResetTimestamp > 0) {
        const firedDate = new Date(riskControl.dailyResetTimestamp);
        const hh = String(firedDate.getUTCHours()).padStart(2, '0');
        const mm = String(firedDate.getUTCMinutes()).padStart(2, '0');
        const ss = String(firedDate.getUTCSeconds()).padStart(2, '0');
        setDailyResetFiredToday(true);
        setDailyResetFiredAt(`${hh}:${mm}:${ss} UTC`);
        const nextReset = new Date(resetToday);
        nextReset.setUTCDate(nextReset.getUTCDate() + 1);
        const diff = nextReset.getTime() - now.getTime();
        const h = Math.floor(diff / 3600000);
        const m = Math.floor((diff % 3600000) / 60000);
        const s = Math.floor((diff % 60000) / 1000);
        setDailyCountdown(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
        return;
      }

      setDailyResetFiredToday(false);
      setDailyResetFiredAt('');

      let target: Date;
      if (now >= resetToday) {
        target = new Date(resetToday);
        target.setUTCDate(target.getUTCDate() + 1);
      } else {
        target = resetToday;
      }

      const diff = target.getTime() - now.getTime();
      if (diff <= 0) {
        setDailyCountdown('00:00:00');
        return;
      }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      setDailyCountdown(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`);
    };

    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [riskControl.dailyResetDate, riskControl.dailyResetTimestamp]);

  // ─── Derived data ───────────────────────────────────────────

  const symbolList = engineState?.symbols ? Object.values(engineState.symbols) : [];
  const connected = engineState?.connected ?? false;
  const isLoading = engineState?.loading ?? false;
  const errorMsg = engineState?.lastError || '';

  const currentAccount = ctraderAccounts.find(
    (a) => String(a.account_id) === String(selectedAccountId)
  );

  const displaySymbols = selectedList ? selectedList.symbols : [];

  return {
    symbolLists,
    loadingLists,
    selectedListId,
    selectedList,
    dropdownOpen,
    setDropdownOpen,
    dropdownRef,
    selectedAccountId,
    engineState,
    isEngineRunning,
    statusMap,
    openStatusDropdown,
    setOpenStatusDropdown,
    statusDropdownRef,
    openNoteSymbol,
    setOpenNoteSymbol,
    playMap,
    symbolConfigs,
    workspaceLoading,
    dailyCountdown,
    dailyResetFiredToday,
    dailyResetFiredAt,
    forceCheckResults,
    forceCheckRunning,
    forceCheckPanelOpen,
    setForceCheckPanelOpen,
    setForceCheckResults,
    testTelegramRunning,
    testTelegramResult,
    orderStatuses,
    offEngineStatuses,
    activityLog,
    setActivityLog,
    showActivityLog,
    setShowActivityLog,
    tma50AlertOn,
    convergenceAlertOn,
    boxVsTma50AlertOn,
    rule4Enabled,
    saveStatus,
    connected,
    isLoading,
    errorMsg,
    currentAccount,
    displaySymbols,
    activeSymbolIds,
    riskControl,
    handleSelectList,
    handleResetAll,
    handleForceCheckAll,
    handleToggleTma50Alert,
    handleToggleConvergenceAlert,
    handleToggleBoxVsTma50Alert,
    handleToggleRule4,
    handleTestTelegram,
    handleStatusChange,
    handlePriceChange,
    handleMaxChange,
    handleVolumeChange,
    handleBoxXChange,
    handleBoxAlertToggle,
    handleNoteChange,
    handleDirectionChange,
    handleOrderDirectionChange,
    handleResetSymbol,
    startEngine,
  };
}